import React, { useCallback, useState } from 'react';
import { Button } from './ui/button';
import { Upload, FileText, Check } from 'lucide-react';
import { cn } from '../lib/utils';

interface FileUploadProps {
  onFileUpload: (file: File) => void;
  disabled?: boolean;
}

export const FileUpload: React.FC<FileUploadProps> = ({ onFileUpload, disabled }) => {
  const [dragOver, setDragOver] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (disabled) return;

    const files = Array.from(e.dataTransfer.files);
    const csvFile = files.find(file => file.type === 'text/csv' || file.name.endsWith('.csv'));
    
    if (csvFile) {
      setUploadedFile(csvFile);
      onFileUpload(csvFile);
    }
  }, [onFileUpload, disabled]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setUploadedFile(file);
      onFileUpload(file);
    }
  }, [onFileUpload]);

  if (uploadedFile && disabled) {
    return (
      <div>
        <div className="border-2 border-green-200 bg-green-50 rounded-lg p-6">
          <div className="flex items-center gap-3 text-green-700">
            <Check className="w-5 h-5" />
            <FileText className="w-5 h-5" />
            <span className="font-medium">{uploadedFile.name}</span>
            <span className="text-sm text-green-600">
              ({(uploadedFile.size / 1024).toFixed(1)} KB)
            </span>
          </div>
        </div>
        <div className="mt-4 text-center text-sm text-gray-600">
          Want to connect to HubSpot or Salesforce directly? Set up a connection under <a href="/settings" className="text-blue-600 hover:underline">Settings</a>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
          dragOver && !disabled ? "border-primary bg-primary/5" : "border-gray-300",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-primary hover:bg-primary/5"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-lg font-medium mb-2">
          Drag and drop your CSV file here
        </p>
        <p className="text-gray-500 mb-4">or</p>
        <Button variant="outline" disabled={disabled}>
          <label className="cursor-pointer text-foreground">
            Browse Files
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              disabled={disabled}
            />
          </label>
        </Button>
        <p className="text-sm text-gray-500 mt-4">
          Only CSV files are supported
        </p>
      </div>
      <div className="mt-4 text-center text-sm text-gray-600">
        Want to connect to HubSpot or Salesforce directly? Set up a connection under <a href="/settings" className="text-blue-600 hover:underline">Settings</a>
      </div>
    </div>
  );
}; 